/**
 * DORMANT for the partials flow. Since #21217/#21220 the live partial payload
 * arrives ALREADY accumulated and deduplicated by the server
 * (`apps/processor/reports_processor/partial_accumulator.py`), and the frontend
 * only encrypts what it received and renders it — it never merges. This helper
 * is deliberately kept, unused by that path, because it still serves the
 * "Buscar mais dados" flow (#21219), which stays in the codebase and tested
 * with its UI entry point hidden. Do not reintroduce it into the partials path:
 * a duplicate item reaching the screen is a server-side defect by contract.
 *
 * Item-level merge/dedup for an incremental "Buscar mais dados" result
 * against a section's already-rendered `data` (task #21220).
 *
 * The backend does NOT dedup the returned array against what the client
 * already has — it can't: content is end-to-end encrypted, so the backend
 * never sees plaintext to compare (zero-knowledge). `event.section.data`
 * arriving over the socket is the FULL raw result of one fetch-more call,
 * not a pre-computed delta, despite `docs/vault/07-reference/
 * redes-sociais-dispatch.md`'s aspirational wording ("o merge dedupa...") —
 * verified against `section_result_consumer.py` (task #21217), which does
 * no such comparison. This is therefore the actual (and only) line of
 * defense against duplicate items appearing on screen.
 */
import { lookupFirstLabelValue } from "_@snap/reports/helpers/redes-sociais-detalhes-lookup.helper";

type SectionEntry = Record<string, any>;

function entryDetalhes(entry: SectionEntry): Record<string, { value?: unknown }> | undefined {
  return entry && typeof entry === "object" ? entry.detalhes : undefined;
}

/**
 * Stable dedup key for one entry: the first matching identity label's value
 * when `identityLabels` resolves one, else a structural fallback (sorted
 * JSON of every field value) so sections with no defined identity field
 * (today: the LinkedIn-only ones) still dedup exact repeats instead of
 * accumulating literal duplicates forever.
 */
function entryIdentityKey(entry: SectionEntry, identityLabels: readonly string[]): string {
  const detalhes = entryDetalhes(entry);
  const byLabel = identityLabels.length > 0 ? lookupFirstLabelValue(detalhes, identityLabels) : undefined;
  if (byLabel) {
    return `id:${byLabel}`;
  }
  const values = detalhes
    ? Object.keys(detalhes)
        .sort()
        .map((label) => [label, detalhes[label]?.value])
    : [];
  return `structural:${JSON.stringify(values)}`;
}

export interface MergeSectionDataResult {
  /** `existing` followed by only the genuinely new `incoming` entries. */
  merged: SectionEntry[];
  /** Count of incoming entries that were NOT already present. */
  addedCount: number;
}

/**
 * Merge an incremental fetch-more result into a section's existing rendered
 * entries, deduping by item identity. Existing order/entries are preserved
 * untouched; new entries are appended in the order Snap returned them.
 * Pure, defensive — never throws on malformed entries.
 */
export function mergeIncrementalEntries(
  existing: SectionEntry[] | undefined | null,
  incoming: SectionEntry[] | undefined | null,
  identityLabels: readonly string[],
): MergeSectionDataResult {
  const existingEntries = Array.isArray(existing) ? existing : [];
  const incomingEntries = Array.isArray(incoming) ? incoming : [];

  const seenKeys = new Set(existingEntries.map((entry) => entryIdentityKey(entry, identityLabels)));
  const newEntries: SectionEntry[] = [];

  for (const entry of incomingEntries) {
    const key = entryIdentityKey(entry, identityLabels);
    if (seenKeys.has(key)) {
      continue;
    }
    seenKeys.add(key);
    newEntries.push(entry);
  }

  return {
    merged: newEntries.length > 0 ? [...existingEntries, ...newEntries] : existingEntries,
    addedCount: newEntries.length,
  };
}
